/**
 * Pure presentation geometry for the planet surface scene (DS-13). No React,
 * no DOM, no wall-clock or nondeterministic randomness — every function here
 * is a deterministic function of its arguments so DS-14+ can build the
 * surface UI on top of known-value math.
 *
 * Deliberately not in `packages/game-engine`: this is viewport/pixel
 * geometry, not a game rule, and the engine has no business knowing about
 * viewports.
 */

export const ZONE_WIDTH = 1240;
export const WORLD_WIDTH = ZONE_WIDTH * 3;
export const ZONE_ORDER = ['resource', 'industry', 'military'] as const;

const LCG_MULTIPLIER = 1103515245;
const LCG_INCREMENT = 12345;
/**
 * 2^32, not 2^31. The state must be multiplied with `Math.imul` and reduced
 * with `>>> 0`: a plain `state * LCG_MULTIPLIER` reaches ~2.4e18, which is
 * 263x past Number.MAX_SAFE_INTEGER, so the low bits are lost to floating
 * point and the recurrence collapses. Hull-Dobell holds for these
 * parameters (multiplier % 4 === 1, odd increment), giving the full period.
 */
const LCG_MODULUS = 4294967296;

const VELOCITY_DECAY = 0.93;
const VELOCITY_STOP_THRESHOLD = 0.3;

const STEP_EASE = 0.16;
const STEP_SNAP_THRESHOLD = 0.6;

/** Left/Right arrow key scroll step, px. Shared by `PlanetSurface`'s stage and `ZoneStrip`'s slider (DS-16). */
export const ARROW_STEP_PX = 200;

export interface Rock {
    /** Horizontal position, percent of the ground band's width (0..100). */
    x: number;
    /** Vertical position in px, always within [groundTop, groundTop + groundHeight]. */
    y: number;
    /** Rock size in px. */
    size: number;
    /** Shading value 0..1 (0 = darkest, 1 = lightest) used for a fill/shadow tint. */
    shade: number;
}

/** Deterministic LCG. Same seed → same sequence, forever. */
export function createRng(seed: number): () => number {
    let state = Math.floor(seed) >>> 0;
    return () => {
        state = (Math.imul(state, LCG_MULTIPLIER) + LCG_INCREMENT) >>> 0;
        return state / LCG_MODULUS;
    };
}

/**
 * FNV-1a 32-bit — turns a planet id (a Firestore doc id, not a number) into
 * a `createRng` seed. Same id → same hash → same scatter, forever; two
 * different planets almost never collide (2^32 buckets). Not for anything
 * security-sensitive — it's a scatter seed, not a checksum.
 */
export function hashSeed(id: string): number {
    let hash = 0x811c9dc5;
    for (let i = 0; i < id.length; i++) {
        hash ^= id.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193);
    }
    return hash >>> 0;
}

export interface SurfaceProp {
    /** A race-specific prop key, e.g. 'boulders' (humans/robots) or 'organic-3' (axons) — see surfaceProps.ts. */
    kind: string;
    /** Horizontal position, percent of the world's width (0..100) — same convention as `Rock.x`. */
    x: number;
    /** Vertical position in px, always within [groundTop, groundTop + groundHeight]. */
    y: number;
    /** Size multiplier applied to the prop's own base render size. */
    scale: number;
    /** Mirror horizontally, purely for visual variety between repeats of the same kind. */
    flip: boolean;
}

/** An axis-aligned world-space exclusion rectangle a prop's contact point must not fall inside — see `surfacePlotAvoidRects` in `surfaceLayout.ts`. */
export interface SurfaceAvoidRect {
    left: number;
    right: number;
    top: number;
    bottom: number;
}

/**
 * One curated placement pocket for `scatterSurfaceProps` (DS-29 issue #227
 * round 9) — a world-space box near a specific plot, plus the exclusion
 * rects that box must still respect. See `surfacePropAreas` in
 * `surfaceLayout.ts` for how the actual 12 areas are built; this module
 * only needs the resulting geometry, not which plot each one is named
 * after.
 */
export interface SurfacePropArea {
    /** Which zone's kind pool this area draws from. */
    zone: (typeof ZONE_ORDER)[number];
    left: number;
    right: number;
    top: number;
    bottom: number;
    /**
     * Every *other* plot's exclusion rect — deliberately never this area's
     * own anchor plot, since the area exists specifically to sit near it.
     */
    avoidRects: readonly SurfaceAvoidRect[];
    /**
     * The vertical span this area's own anchor tile occupies, clearance
     * included. `top`/`bottom` above already keep a prop's *contact point*
     * out of it, but a prop is drawn upward from that point, so one standing
     * below the tile can still reach back into it. The scatter tests each
     * candidate's real rendered footprint against this.
     *
     * Kept as a span for the scatter to check rather than baked into
     * `top`/`bottom` as a worst-case prop height: reserving the tallest
     * possible prop throws the whole pocket away on a short stage, where a
     * smaller roll of the same prop would have fitted.
     */
    anchorReserved: { top: number; bottom: number };
}

/**
 * How many times a placement search retries a fresh position within its
 * range before giving up on that range, rather than forcing an overlap.
 * Raised from 8 (DS-29 issue #227 round 8: "terrain is almost empty" — the
 * round-7 size-aware checks made a lot more candidates fail, so more
 * retries per range meaningfully helps recover the placements those checks
 * now correctly reject).
 */
const PROP_PLACEMENT_MAX_ATTEMPTS = 16;

/** Prop render-size multiplier range, applied on top of `propDepthScale`'s foreground/background factor. */
export const PROP_SCALE_MIN = 0.7;
export const PROP_SCALE_MAX = 1.3;

/** Foreground props render bigger than background ones, same idea as `projectTile`'s depth scale for buildings — otherwise a prop right at the camera reads no bigger than one near the horizon. */
/** Smallest a prop may render before it is not worth drawing at all, px — the floor the anchor-clearance cap is allowed to shrink toward on a short stage. */
const MIN_PROP_RENDER_HEIGHT_PX = 24;
const PROP_DEPTH_SCALE_MIN = 0.5;
const PROP_DEPTH_SCALE_MAX = 1.15;

/**
 * DS-29 issue #227 round 7: "props overlap each other" / "props violate
 * empty lots and building spacing" — despite the round-3 avoid-rect and
 * round-5 min-spacing work, both of those checks only ever tested a prop's
 * single anchor *point*, never its actual rendered footprint. A prop can
 * render up to `PROP_TARGET_HEIGHT_PX * PROP_SCALE_MAX * PROP_DEPTH_SCALE_MAX`
 * px tall (see `PlanetSurface.tsx`) with a similar real pixel width — tens
 * of px on either side of its anchor — so an anchor that legitimately
 * cleared a plot's rect, or another prop's anchor by the old flat 46px,
 * could still *render* right on top of it. This module has no access to
 * any sprite's real image dimensions (it's pure geometry, no DOM, and a
 * `kind` is just an opaque string), so `PROP_WIDTH_TO_HEIGHT_ESTIMATE` is a
 * stand-in, not any one sprite's true aspect ratio — measured directly from
 * every race's real terrain sprite files (16 each,
 * `assets/images/{race}/terrain/*.png`) instead of guessed.
 *
 * That measurement is the median (1.20 across all 48 files), not the
 * maximum (~1.70, robots' `2-1.png`). A first attempt at this constant
 * (0.85) undershot the *typical* sprite badly and let real sprites' rendered
 * edges clip a neighbour or a plot the estimate said was clear; going all
 * the way to the measured maximum (round 7) swung too far the other way —
 * "terrain is almost empty, props only appear at zone borders" (round 8) —
 * because every candidate reserved as if it were the single widest sprite
 * in the entire set, on every attempt. The median means most sprites get an
 * accurate-to-generous margin; the widest ~25% (above 1.3, per the same
 * measurement) can occasionally still graze a neighbour by a few px — an
 * acceptable trade for background clutter not being reduced to a handful of
 * props clustered at zone edges.
 */
export const PROP_WIDTH_TO_HEIGHT_ESTIMATE = 1.2;
/** Extra clearance beyond two props' own estimated half-widths, so they don't render edge-to-edge touching even when technically not overlapping. */
const PROP_SPACING_BUFFER_PX = 10;

interface PlacedFootprint {
    worldX: number;
    halfWidth: number;
    yTop: number;
    yBottom: number;
}

/** True if a prop of the given footprint, anchored at `worldX`/`y`, would render inside `rect` — full-height columns (see `surfacePlotAvoidRects`) mean the vertical check is almost always true in practice; it's here for correctness with an arbitrary caller-supplied rect. */
function overlapsRect(worldX: number, halfWidth: number, y: number, rect: SurfaceAvoidRect): boolean {
    if (y < rect.top || y > rect.bottom) return false;
    return worldX + halfWidth >= rect.left && worldX - halfWidth <= rect.right;
}

/** True if two props' estimated rendered footprints (axis-aligned boxes, anchored at their contact point and extending upward) would visually intersect. */
function overlapsPlacedProp(worldX: number, halfWidth: number, yTop: number, yBottom: number, other: PlacedFootprint): boolean {
    const xOverlap = Math.abs(worldX - other.worldX) < halfWidth + other.halfWidth + PROP_SPACING_BUFFER_PX;
    const yOverlap = yTop < other.yBottom && other.yTop < yBottom;
    return xOverlap && yOverlap;
}

/**
 * A prop's estimated rendered footprint at a candidate position — half-width
 * and top edge, both derived from the same height calc `PlanetSurface.tsx`
 * uses to actually render it (`basePropHeightPx * scale * propDepthScale`),
 * so the collision checks above reason about roughly what will really be on
 * screen instead of a flat per-prop constant.
 */
function propFootprint(
    basePropHeightPx: number,
    scale: number,
    y: number,
    groundTop: number,
    groundHeight: number,
): { halfWidth: number; yTop: number } {
    const height = basePropHeightPx * scale * propDepthScale(y, groundTop, groundHeight);
    return { halfWidth: (height * PROP_WIDTH_TO_HEIGHT_ESTIMATE) / 2, yTop: y - height };
}

/**
 * Worst-case rendered height of a prop whose base render height (before
 * `PROP_SCALE_MIN..MAX` and `propDepthScale`) is `basePropHeightPx` — the
 * top margin `scatterSurfaceProps` needs to reserve below `groundTop` so a
 * background prop can never grow tall enough to poke its top edge above the
 * horizon (DS-29 issue #227 round 5: "some props appear outside of terrain
 * area, above land"). Uses `PROP_SCALE_MAX`, not `_MIN`: depth (how far from
 * the camera) and size-roll (how big for its depth) are independent draws,
 * so a background prop can still roll the largest size multiplier.
 *
 * The margin has to be solved for, not just multiplied out: the prop this
 * reserves room for stands at `groundTop + margin`, not at the horizon, and
 * `propDepthScale` has already grown by the time it gets there. Reserving
 * `base * scale * PROP_DEPTH_SCALE_MIN` assumed the horizon's own depth
 * scale and so under-reserved, letting a prop poke a few px above the
 * skyline. Requiring `margin >= height(margin)` and solving gives:
 *
 *   margin >= base*s*A / (1 - base*s*(B - A) / groundHeight)
 *
 * for `A = PROP_DEPTH_SCALE_MIN`, `B = PROP_DEPTH_SCALE_MAX`. A ground band
 * short enough to make that denominator non-positive cannot fit the tallest
 * prop at all, so the whole band is reserved and every pocket is skipped.
 */
export function propTopMargin(basePropHeightPx: number, groundHeight: number): number {
    const tallest = basePropHeightPx * PROP_SCALE_MAX;
    if (tallest <= 0) return 0;
    if (groundHeight <= 0) return 0;
    const growth = (tallest * (PROP_DEPTH_SCALE_MAX - PROP_DEPTH_SCALE_MIN)) / groundHeight;
    if (growth >= 1) return groundHeight;
    return (tallest * PROP_DEPTH_SCALE_MIN) / (1 - growth);
}

/**
 * Scattered ground-clutter props (DS-29 issue #227), one at most per
 * caller-supplied `area` — round 9's redesign, replacing every earlier
 * version's global "search the whole ground band, avoid every plot's
 * column" model.
 *
 * That global model (rounds 3-8) had a structural ceiling: `resource` and
 * `industry` zones' plots sit closer together (120-137px) than any
 * defensible plot-exclusion half-width (170px+, see `surfaceLayout.ts`'s
 * `PLOT_AVOID_MIN_HALF_WIDTH_PX`), so their entire interior was
 * permanently unplaceable no matter how a prop's own footprint estimate
 * got tuned — a prop could only ever land in the ~100px margins at each
 * zone's start/end. Loosening the collision math (round 7) or the
 * footprint estimate (round 8) only changed how much of that leftover
 * margin got used; it never touched the underlying zero-interior-room
 * problem, which is why props kept reading as clustered at zone borders
 * ("round 8: terrain is almost empty, props only appear at zone borders").
 *
 * The fix: stop searching for open ground and place props at specific,
 * curated positions relative to specific plots instead — see
 * `surfacePropAreas` in `surfaceLayout.ts` for the actual 12-area table
 * (product-specified: "above lot 1", "below lot 6 up close", etc). Each
 * `area` already carries its own `avoidRects` (every *other* plot's
 * exclusion rect — never its own anchor plot's, since the whole point is
 * sitting near that plot) and a vertical band picked to read as background
 * ("above" / "in the distance") or foreground ("below" / "up close")
 * relative to it. This function's job shrinks to: for each area, pick one
 * still-unused kind valid in that area's zone, and roll a position inside
 * the area that clears its `avoidRects` and every already-placed prop's
 * estimated footprint (`propFootprint`/`overlapsRect`/`overlapsPlacedProp`,
 * unchanged since round 7) — retrying up to `PROP_PLACEMENT_MAX_ATTEMPTS`
 * times before giving up on that one area.
 *
 * Kind and area order are both seeded-shuffled, so which of the (up to 16)
 * kinds lands in which of the 12 areas varies by seed/planet without ever
 * repeating one (same "at most once" rule as round 5).
 *
 * `basePropHeightPx` keeps the job it had before (round 5/7): letting
 * `propFootprint`/`propTopMargin` reason about a candidate's real rendered
 * size, so a background prop can't poke its top above the horizon and two
 * props' rendered boxes can't visually overlap. Defaults to 0 for callers
 * that don't need either guarantee (e.g. tests exercising placement
 * geometry in isolation).
 */
export function scatterSurfaceProps(
    seed: number,
    areas: readonly SurfacePropArea[],
    groundTop: number,
    groundHeight: number,
    kindsByZone: Readonly<Record<(typeof ZONE_ORDER)[number], readonly string[]>>,
    basePropHeightPx: number = 0,
): SurfaceProp[] {
    const rng = createRng(seed);

    // The union of every zone's kinds, deduplicated in first-seen order —
    // the master pool this call draws at most one instance of each from.
    const seenKinds = new Set<string>();
    const allKinds: string[] = [];
    for (const zone of ZONE_ORDER) {
        for (const kind of kindsByZone[zone]) {
            if (!seenKinds.has(kind)) {
                seenKinds.add(kind);
                allKinds.push(kind);
            }
        }
    }
    if (allKinds.length === 0 || areas.length === 0) return [];

    // Seeded Fisher-Yates over both the kind pool and the area list — which
    // kind lands in which area is deterministic per `seed` but not a boring
    // declaration-order pairing.
    for (let i = allKinds.length - 1; i > 0; i--) {
        const j = Math.floor(rng() * (i + 1));
        const swap = allKinds[i]!;
        allKinds[i] = allKinds[j]!;
        allKinds[j] = swap;
    }
    const shuffledAreas = areas.slice();
    for (let i = shuffledAreas.length - 1; i > 0; i--) {
        const j = Math.floor(rng() * (i + 1));
        const swap = shuffledAreas[i]!;
        shuffledAreas[i] = shuffledAreas[j]!;
        shuffledAreas[j] = swap;
    }

    const topMarginPx = propTopMargin(basePropHeightPx, groundHeight);
    const usedKinds = new Set<string>();
    const props: SurfaceProp[] = [];
    const placed: PlacedFootprint[] = [];

    for (const area of shuffledAreas) {
        const kind = allKinds.find((candidate) => !usedKinds.has(candidate) && kindsByZone[area.zone].includes(candidate));
        if (!kind) continue;

        // Clamp on both sides, then skip if nothing survives. The horizon
        // guard can leave no safe room in a pocket sitting close to the
        // horizon (a previous `Math.max(yMin, area.bottom)` here forced yMax
        // back *up* to yMin, degenerating to a single unsafe y outside the
        // pocket's own span), and a pocket whose anchor sits low can reach
        // past the foot of the ground band, which would break `SurfaceProp.y`'s
        // "always within [groundTop, groundTop + groundHeight]" contract.
        const yMin = Math.max(area.top, groundTop + topMarginPx);
        const yMax = Math.min(area.bottom, groundTop + groundHeight);
        if (yMax < yMin) continue;
        const xWidth = area.right - area.left;

        let accepted: { x: number; y: number; worldX: number; scale: number; halfWidth: number; yTop: number } | null = null;
        for (let attempt = 0; attempt < PROP_PLACEMENT_MAX_ATTEMPTS; attempt++) {
            const worldX = area.left + rng() * xWidth;
            const y = yMin + rng() * (yMax - yMin);

            // A prop is drawn upward from its contact point, so one standing
            // below its anchor tile can reach back into it. Rather than
            // reserving the tallest possible prop in the band — which throws
            // the whole pocket away on a short stage, where a smaller roll of
            // the same prop would have fitted — cap the size roll to the room
            // this candidate actually has. Only a pocket with no room for even
            // the smallest prop is skipped.
            const depth = propDepthScale(y, groundTop, groundHeight);
            const rolled = PROP_SCALE_MIN + rng() * (PROP_SCALE_MAX - PROP_SCALE_MIN);
            let scale = rolled;
            if (y > area.anchorReserved.top && basePropHeightPx > 0 && depth > 0) {
                scale = Math.min(scale, (y - area.anchorReserved.bottom) / (basePropHeightPx * depth));
            }
            // The cap is allowed to take the prop below `PROP_SCALE_MIN`: on a
            // short stage the room below a tile is smaller than the smallest
            // normal roll, and a smaller prop reads better than no prop. Only
            // a pocket that cannot fit even a legible one is skipped.
            if (basePropHeightPx > 0 && basePropHeightPx * scale * depth < MIN_PROP_RENDER_HEIGHT_PX) continue;
            const { halfWidth, yTop } = propFootprint(basePropHeightPx, scale, y, groundTop, groundHeight);
            if (area.avoidRects.some((rect) => overlapsRect(worldX, halfWidth, y, rect))) continue;
            if (placed.some((other) => overlapsPlacedProp(worldX, halfWidth, yTop, y, other))) continue;

            accepted = { x: (worldX / WORLD_WIDTH) * 100, y, worldX, scale, halfWidth, yTop };
            break;
        }
        if (!accepted) continue;

        usedKinds.add(kind);
        placed.push({ worldX: accepted.worldX, halfWidth: accepted.halfWidth, yTop: accepted.yTop, yBottom: accepted.y });
        props.push({
            kind,
            x: accepted.x,
            y: accepted.y,
            scale: accepted.scale,
            flip: rng() > 0.5,
        });
    }
    return props;
}

/**
 * Size multiplier for a prop at ground-y `y`, foreground bigger than
 * background — the same visual logic `projectTile` applies to buildings via
 * `depth`, but props only carry a `y` (DS-29 issue #227: up close, props read
 * as tiny next to a foreground building that itself scales up).
 */
export function propDepthScale(y: number, groundTop: number, groundHeight: number): number {
    const t = groundHeight > 0 ? (y - groundTop) / groundHeight : 0;
    const clamped = Math.min(Math.max(t, 0), 1);
    return PROP_DEPTH_SCALE_MIN + clamped * (PROP_DEPTH_SCALE_MAX - PROP_DEPTH_SCALE_MIN);
}

export interface TerrainPatch {
    /** Horizontal position, percent of the ground band's width (0..100). */
    x: number;
    /** Vertical centre in px, always within [groundTop, groundTop + groundHeight]. */
    y: number;
    /** Patch width in px. */
    width: number;
    /** Patch height in px. */
    height: number;
    /** Rotation, degrees. */
    rotation: number;
    /** An eight-value CSS `border-radius` shorthand giving the patch an irregular, non-elliptical outline. */
    radii: string;
    /** Fill opacity — kept low so these read as ground shading, not objects. */
    opacity: number;
}

const TERRAIN_PATCH_MIN_WIDTH = 90;
const TERRAIN_PATCH_MAX_WIDTH = 310;
const TERRAIN_PATCH_MIN_ASPECT = 0.35;
const TERRAIN_PATCH_MAX_ASPECT = 0.6;
const TERRAIN_PATCH_MIN_RADIUS_PCT = 30;
const TERRAIN_PATCH_MAX_RADIUS_PCT = 70;
const TERRAIN_PATCH_MIN_OPACITY = 0.08;
const TERRAIN_PATCH_MAX_OPACITY = 0.22;
const TERRAIN_PATCH_CORNERS = 8;

/**
 * Soft, irregularly-shaped dark patches scattered across the ground, faking
 * the look of uneven terrain (craters, mounds, mining scars) on what is
 * otherwise a flat gradient band (DS-29 issue #227). Not real geometry —
 * each patch is a blob-shaped div (an eight-value `border-radius`, not an
 * ellipse) rendered low-opacity and blurred, so it reads as a shadow cast by
 * unevenness rather than as a sticker sitting on the ground.
 */
export function scatterTerrainPatches(seed: number, count: number, groundTop: number, groundHeight: number): TerrainPatch[] {
    const rng = createRng(seed);
    const patches: TerrainPatch[] = [];
    for (let i = 0; i < count; i++) {
        const width = TERRAIN_PATCH_MIN_WIDTH + rng() * (TERRAIN_PATCH_MAX_WIDTH - TERRAIN_PATCH_MIN_WIDTH);
        const aspect = TERRAIN_PATCH_MIN_ASPECT + rng() * (TERRAIN_PATCH_MAX_ASPECT - TERRAIN_PATCH_MIN_ASPECT);
        const radii = Array.from(
            { length: TERRAIN_PATCH_CORNERS },
            () => `${Math.round(TERRAIN_PATCH_MIN_RADIUS_PCT + rng() * (TERRAIN_PATCH_MAX_RADIUS_PCT - TERRAIN_PATCH_MIN_RADIUS_PCT))}%`,
        );
        patches.push({
            x: rng() * 100,
            y: groundTop + rng() * groundHeight,
            width,
            height: width * aspect,
            rotation: rng() * 360,
            radii: `${radii[0]} ${radii[1]} ${radii[2]} ${radii[3]} / ${radii[4]} ${radii[5]} ${radii[6]} ${radii[7]}`,
            opacity: TERRAIN_PATCH_MIN_OPACITY + rng() * (TERRAIN_PATCH_MAX_OPACITY - TERRAIN_PATCH_MIN_OPACITY),
        });
    }
    return patches;
}

/**
 * The largest reachable scroll offset. The world cannot scroll past the point
 * where its right edge meets the viewport's, so this is the real upper bound
 * of the scroll range — not WORLD_WIDTH, which no viewport can ever reach.
 */
export function maxScroll(viewportWidth: number): number {
    return Math.max(0, WORLD_WIDTH - viewportWidth);
}

/** Scroll clamped to [0, maxScroll(viewportWidth)], never negative. */
export function clampScroll(scrollX: number, viewportWidth: number): number {
    return Math.min(Math.max(scrollX, 0), maxScroll(viewportWidth));
}

/** Which zone the viewport centre is over. Always 0..2. */
export function zoneIndexAt(scrollX: number, viewportWidth: number): 0 | 1 | 2 {
    const clamped = clampScroll(scrollX, viewportWidth);
    const centre = clamped + viewportWidth / 2;
    const index = Math.floor(centre / ZONE_WIDTH);
    return Math.min(Math.max(index, 0), 2) as 0 | 1 | 2;
}

/** Scroll offset that centres the given zone. */
export function scrollForZone(index: number, viewportWidth: number): number {
    const clampedIndex = Math.min(Math.max(Math.floor(index), 0), 2);
    const zoneCentre = clampedIndex * ZONE_WIDTH + ZONE_WIDTH / 2;
    return clampScroll(zoneCentre - viewportWidth / 2, viewportWidth);
}

/** depth 0 = foreground, 1 = far background. */
export function projectTile(
    depth: number,
    groundTop: number,
    groundHeight: number,
): { scale: number; top: number; zIndex: number; opacity: number } {
    return {
        // Depth spread was 1.0 down to 0.79 at the deepest plot in use, which
        // is not enough separation to read as distance — near and far
        // buildings looked the same size. 0.62 puts the farthest plots at
        // ~0.69, so the back row is visibly further away.
        scale: 1 - depth * 0.62,
        top: groundTop + 40 + (1 - depth) * (groundHeight - 96),
        zIndex: Math.round(100 + (1 - depth) * 120),
        opacity: 1 - depth * 0.22,
    };
}

/** Horizon at 50% of viewport height; ground fills the rest. */
export function sceneGeometry(viewportHeight: number): {
    horizon: number;
    groundTop: number;
    groundHeight: number;
} {
    const horizon = viewportHeight * 0.5;
    return {
        horizon,
        groundTop: horizon,
        groundHeight: viewportHeight - horizon,
    };
}

/** Parallax offset for a layer, given its depth factor. */
export function parallaxOffset(scrollX: number, factor: number): number {
    return scrollX * factor;
}

/**
 * How wide a parallax layer has to be to stay flush with the viewport across
 * the whole scroll range. A layer at `factor` only ever travels
 * `factor * (WORLD_WIDTH - viewportWidth)`, so a layer sized at WORLD_WIDTH
 * keeps its far end permanently off-screen — and the narrower the viewport,
 * the larger the unreachable remainder. At factor 1 this is WORLD_WIDTH.
 */
export function parallaxSpan(factor: number, viewportWidth: number): number {
    const travel = Math.max(0, WORLD_WIDTH - viewportWidth);
    return Math.max(0, viewportWidth) + factor * travel;
}

/**
 * Where a child anchored at `worldX` sits inside a layer scrolling at
 * `factor`, so that the world's left and right ends line up with the scroll
 * range's ends at any viewport width. At factor 1 this is `worldX` itself.
 */
export function parallaxAnchor(worldX: number, factor: number, viewportWidth: number): number {
    return (worldX / WORLD_WIDTH) * parallaxSpan(factor, viewportWidth);
}

/** Zone banner width: a full zone on desktop, never wider than the viewport. */
export function bannerWidth(viewportWidth: number): number {
    return Math.min(ZONE_WIDTH, Math.max(0, viewportWidth));
}

/**
 * Left edge of zone `index`'s banner inside a layer scrolling at `factor`.
 * Anchored to the scroll offset that selects the zone rather than to the raw
 * world X, so the banner is centred when its zone is active at every viewport
 * width instead of drifting out of frame on narrow screens.
 */
export function bannerAnchor(index: number, factor: number, viewportWidth: number): number {
    return (
        factor * scrollForZone(index, viewportWidth) +
        (Math.max(0, viewportWidth) - bannerWidth(viewportWidth)) / 2
    );
}

/** Scattered ground rocks: deterministic positions, sizes and shading. */
export function scatterRocks(
    seed: number,
    count: number,
    groundTop: number,
    groundHeight: number,
): Rock[] {
    const rng = createRng(seed);
    const rocks: Rock[] = [];
    for (let i = 0; i < count; i++) {
        rocks.push({
            x: rng() * 100,
            y: groundTop + rng() * groundHeight,
            size: 6 + rng() * 18,
            shade: rng(),
        });
    }
    return rocks;
}

export interface SkyAsteroidPick {
    worldX: number;
    imageIndex: number;
}

/**
 * One seeded position + image choice for a single decorative background
 * asteroid (DS-29 issue #227 round 3: "add one random tiny asteroid from
 * existing images in the sky") — real photo art instead of the flat CSS
 * circle the sky previously used for this object. `imageIndex` is a raw
 * index into whatever image array the caller picked (`Math.floor(rng() *
 * imageCount)`); this function knows nothing about the actual image files.
 */
export function pickSkyAsteroid(seed: number, imageCount: number): SkyAsteroidPick {
    const rng = createRng(seed);
    return {
        worldX: rng() * WORLD_WIDTH,
        imageIndex: Math.floor(rng() * imageCount),
    };
}

/** Seeded star field as a CSS box-shadow value string. */
export function starFieldShadow(seed: number, count: number, width: number, height: number): string {
    const rng = createRng(seed);
    const shadows: string[] = [];
    for (let i = 0; i < count; i++) {
        const x = (rng() * width).toFixed(2);
        const y = (rng() * height).toFixed(2);
        const opacity = (0.35 + rng() * 0.65).toFixed(2);
        shadows.push(`${x}px ${y}px 0 rgba(230,232,245,${opacity})`);
    }
    return shadows.join(', ');
}

/** Momentum: velocity decays by 0.93/frame; stops below 0.3. */
export function decayVelocity(velocity: number): number {
    const next = velocity * VELOCITY_DECAY;
    return Math.abs(next) < VELOCITY_STOP_THRESHOLD ? 0 : next;
}

/** Snap easing toward a target: pos + (target - pos) * 0.16, snapping within 0.6. */
export function stepToward(current: number, target: number): { value: number; done: boolean } {
    const next = current + (target - current) * STEP_EASE;
    if (Math.abs(target - next) < STEP_SNAP_THRESHOLD) {
        return { value: target, done: true };
    }
    return { value: next, done: false };
}

/** Smoothstep, used for depth-based fades. */
export function smoothstep(x: number, edge0: number, edge1: number): number {
    if (edge0 === edge1) return x < edge0 ? 0 : 1;
    const t = Math.min(Math.max((x - edge0) / (edge1 - edge0), 0), 1);
    return t * t * (3 - 2 * t);
}
